/**
 * features/landing/Landing.tsx
 * ------------------------------------------------------------------
 * The public company page for AcePSLE (PTY) Ltd — positioned as a
 * software development company, not as a single product.
 *
 * Structure (one long scroll, anchor-navigated):
 *   intro curtain → sticky nav → cinematic hero with product windows
 *   → domain strip → what we do → selected work (alternating
 *   copy/device case studies) → how we work → stats → why us
 *   → closing CTA → footer
 *
 * Copy and project data live in `work.ts`; styles in `landingCss.ts`.
 * The primary CTA opens the existing public enquiry form (`?enquire`),
 * which writes to `leads` via the submitLead function. Hub sign-in is
 * deliberately secondary.
 * ------------------------------------------------------------------
 */
import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  Mail,
  MapPin,
  Menu,
  Phone,
  X,
} from 'lucide-react';
import AcepsleWordmark from '@/components/branding/AcepsleWordmark';
import { useSiteImages } from '@/features/media/useSiteImages';
import { BrowserFrame } from '@/features/landing/BrowserFrame';
import { LANDING_CSS } from '@/features/landing/landingCss';
import {
  APPROACH,
  COMPANY,
  PROJECTS,
  SCHOOL_PORTAL,
  SERVICES,
  STACK,
  STATS,
  WHY,
  type Project,
} from '@/features/landing/work';

const NAV_LINKS = [
  { href: '#services', label: 'What we do' },
  { href: '#product', label: 'School Portal' },
  { href: '#work', label: 'Work' },
  { href: '#approach', label: 'Approach' },
  { href: '#contact', label: 'Contact' },
];

/* The restructure leads with what we make, so the nav says so. */
const NAV_LINKS_NEXT = [
  { href: '#services', label: 'What we do' },
  { href: '#product', label: 'Products' },
  { href: '#work', label: 'Client work' },
  { href: '#approach', label: 'Approach' },
  { href: '#contact', label: 'Contact' },
];

const ENQUIRE = '?enquire';

/** Case study: copy on one side, the live product on the other. */
function Case({
  project,
  index,
  src,
}: {
  project: Project;
  index: number;
  src?: string;
}) {
  const reversed = index % 2 === 1;
  return (
    <article className={`ac-case${reversed ? ' rev' : ''}`}>
      <div className="ac-reveal">
        <span className="ac-eyebrow">{project.sector}</span>
        <h3 className="ac-h3">
          {project.name}
          {project.kind === 'client' && <span className="ac-tag">Client</span>}
        </h3>
        <p className="ac-case-sum">{project.summary}</p>
        <div className="ac-rule" />
        <p className="ac-body">{project.body}</p>
        <div className="ac-chips">
          {project.chips.map((c) => (
            <span key={c} className="ac-chip">
              {c}
            </span>
          ))}
        </div>
        {project.href && (
          <a className="ac-visit" href={project.href} target="_blank" rel="noreferrer">
            {project.linkLabel ?? `Visit ${project.domain}`} <ArrowUpRight size={15} />
          </a>
        )}
      </div>

      <div className="ac-case-media ac-reveal d1">
        <span className="ac-case-glow" style={{ background: project.accent }} />
        <BrowserFrame
          image={project.image}
          src={src}
          domain={project.domain}
          accent={project.accent}
          alt={`${project.name} — ${project.summary}`}
        />
      </div>
    </article>
  );
}

/**
 * One product, one band, tinted with its own colour.
 *
 * This is what makes School Portal's navy stop looking like an intrusion: a
 * single coloured band among neutral ones reads as a mistake, a rhythm of them
 * reads as a house of brands. The tint is deliberately weak — the accent is a
 * top edge, a dot and the chips, not a saturated background — because seven
 * saturated bands would be a rainbow. The flagship keeps the only full-colour
 * band, above these, so the page reads as one product plus a family.
 */
function BrandBand({ project, index, src }: { project: Project; index: number; src?: string }) {
  const reversed = index % 2 === 1;
  return (
    <section
      className={`ac-band acb-band${reversed ? ' rev' : ''}`}
      style={{ ['--acc' as string]: project.accent }}
    >
      <span className="acb-edge" />
      <div className="ac-wrap acb-grid">
        <div className="ac-reveal">
          <span className="acb-sector">
            <span className="acb-dot" />
            {project.sector}
          </span>
          <h2 className="acb-name">{project.name}</h2>
          <p className="acb-sum">{project.summary}</p>
          <p className="ac-body acb-body">{project.body}</p>
          <div className="ac-chips">
            {project.chips.map((c) => (
              <span key={c} className="ac-chip">
                {c}
              </span>
            ))}
          </div>
          {project.href && (
            <a className="ac-visit" href={project.href} target="_blank" rel="noreferrer">
              {project.linkLabel ?? `Visit ${project.domain}`} <ArrowUpRight size={15} />
            </a>
          )}
        </div>
        <div className="acb-media ac-reveal d1">
          <BrowserFrame
            image={project.image}
            src={src}
            domain={project.domain}
            accent={project.accent}
            alt={`${project.name} — ${project.summary}`}
          />
        </div>
      </div>
    </section>
  );
}

/**
 * Compact product card. Ten full-height case studies made "Selected work"
 * 5,925px — half the whole page — which is what buried the product band sitting
 * just above it. Our own products move into this grid; the three client
 * platforms keep the full treatment, because those are the ones a prospective
 * client reads properly.
 */
function ProductCard({ project, src }: { project: Project; src?: string }) {
  return (
    <article className="acx-card ac-reveal">
      <div className="acx-card-media">
        <BrowserFrame
          image={project.image}
          src={src}
          domain={project.domain}
          accent={project.accent}
          alt={`${project.name} — ${project.summary}`}
        />
      </div>
      <div className="acx-card-body">
        <span className="acx-dot" style={{ background: project.accent }} />
        <h3>{project.name}</h3>
        <p>{project.summary}</p>
        {project.href && (
          <a className="acx-link" href={project.href} target="_blank" rel="noreferrer">
            {project.linkLabel ?? `Visit ${project.domain}`} <ArrowUpRight size={14} />
          </a>
        )}
      </div>
    </article>
  );
}

export function Landing({
  onSignIn,
  variant = 'live',
}: {
  onSignIn: () => void;
  variant?: 'live' | 'a' | 'b' | 'c';
}) {
  /* 'a' restructures the sections; 'b' keeps them and gives every product a
     band of its own, so School Portal's navy reads as one of a set. */
  const next = variant === 'a';
  const brands = variant === 'b';
  const root = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [menu, setMenu] = useState(false);
  // Screenshots uploaded through the admin media library. Absent or
  // still loading simply means each frame shows its built-in fallback.
  const { images } = useSiteImages();

  /* Intro curtain — skipped entirely when reduced motion is requested. */
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setRevealed(true);
      return;
    }
    const t = window.setTimeout(() => setRevealed(true), 1500);
    return () => window.clearTimeout(t);
  }, []);

  /* Nav background appears once the hero starts scrolling away. */
  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Scroll reveals + stat count-ups. */
  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const reveals = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            reveals.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );
    el.querySelectorAll('.ac-reveal, .ac-step').forEach((n) => reveals.observe(n));

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const counters = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const node = e.target as HTMLElement;
          counters.unobserve(node);
          const target = Number(node.dataset.count || '0');
          const suffix = node.dataset.suffix || '';
          if (reduce) {
            node.textContent = target.toString() + suffix;
            return;
          }
          const t0 = performance.now();
          const tick = (t: number) => {
            const p = Math.min(1, (t - t0) / 1300);
            const eased = 1 - Math.pow(1 - p, 3);
            node.textContent = Math.round(target * eased).toString() + suffix;
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }),
      { threshold: 0.6 },
    );
    el.querySelectorAll('[data-count]').forEach((n) => counters.observe(n));

    return () => {
      reveals.disconnect();
      counters.disconnect();
    };
  }, []);

  const hero = PROJECTS[0];
  const heroBack = PROJECTS[2];
  const heroMini = PROJECTS[3];

  return (
    <div ref={root} className={`ac${revealed ? ' ac--in' : ''}`}>
      <style>{LANDING_CSS}</style>

      {/* Intro curtain */}
      <div className="ac-intro" aria-hidden="true">
        <div className="ac-intro-panel l" />
        <div className="ac-intro-panel r" />
        <div className="ac-intro-mark">
          <div className="ac-intro-inner">
            <AcepsleWordmark height={62} variant="reversed" withTagline />
          </div>
        </div>
      </div>

      {/* Nav */}
      <header className={`ac-nav${stuck ? ' stuck' : ''}`}>
        <div className="ac-wrap ac-nav-row">
          <a href="#top" aria-label="Acepsle — home">
            <AcepsleWordmark height={26} variant="reversed" />
          </a>

          <nav className="ac-nav-links">
            {(next || brands ? NAV_LINKS_NEXT : NAV_LINKS).map((l) => (
              <a key={l.href} href={l.href}>
                {l.label}
              </a>
            ))}
          </nav>

          <div className="ac-nav-cta">
            <button className="ac-btn ac-btn-line ac-btn-sm" onClick={onSignIn}>
              Sign in
            </button>
            <a className="ac-btn ac-btn-brand ac-btn-sm" href={ENQUIRE}>
              Start a project
            </a>
          </div>

          <button
            className="ac-burger"
            onClick={() => setMenu((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={menu}
          >
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <div className={`ac-mobile${menu ? ' open' : ''}`}>
          <div className="ac-wrap">
            {(next || brands ? NAV_LINKS_NEXT : NAV_LINKS).map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMenu(false)}>
                {l.label}
              </a>
            ))}
            <a
              href="#top"
              onClick={(e) => {
                e.preventDefault();
                setMenu(false);
                onSignIn();
              }}
            >
              Sign in to the Hub
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="ac-hero" id="top">
        <div className="ac-hero-bg">
          <span className="ac-glow ac-glow-1" />
          <span className="ac-glow ac-glow-2" />
        </div>

        <div className="ac-wrap ac-hero-grid">
          <div>
            <span className="ac-eyebrow ac-enter e1">
              <Building2 size={13} /> {COMPANY.tagline}
            </span>
            <h1 className="ac-h1 ac-enter e2" style={{ marginTop: 24 }}>
              We build the software
              <br />
              Botswana runs on.
            </h1>
            <p className="ac-lead ac-enter e3">
              {COMPANY.tradingName} is a Gaborone software company. We design and build
              complete web and mobile platforms — school management systems, business
              and finance suites, booking engines and consumer apps — end to end,
              in-house, for organisations here at home.
            </p>
            <div className="ac-hero-cta ac-enter e4">
              <a className="ac-btn ac-btn-brand" href={ENQUIRE}>
                Start a project <ArrowRight size={16} />
              </a>
              <a className="ac-btn ac-btn-line" href="#work">
                See our work
              </a>
            </div>
            <div className="ac-hero-meta ac-enter e5">
              <span>
                <b>9</b> platforms shipped
              </span>
              <span>
                <b>3</b> client organisations
              </span>
              <span>
                <b>Gaborone</b> — built &amp; supported locally
              </span>
            </div>
          </div>

          {/* Product window cluster */}
          <div className="ac-stack ac-enter e4">
            <div className="ac-stack-back">
              <BrowserFrame
                image={heroBack.image}
                src={images[heroBack.id]?.url}
                domain={heroBack.domain}
                accent={heroBack.accent}
                alt={`${heroBack.name} — ${heroBack.summary}`}
              />
            </div>
            <div className="ac-stack-front ac-float">
              <BrowserFrame
                image={hero.image}
                src={images[hero.id]?.url}
                domain={hero.domain}
                accent={hero.accent}
                alt={`${hero.name} — ${hero.summary}`}
              />
            </div>
            <div className="ac-stack-mini ac-float2">
              <BrowserFrame
                image={heroMini.image}
                src={images[heroMini.id]?.url}
                domain={heroMini.domain}
                accent={heroMini.accent}
                alt={`${heroMini.name} — ${heroMini.summary}`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Domain strip */}
      <div className="ac-strip">
        <div className="ac-wrap ac-strip-row">
          {PROJECTS.filter((p) => p.href).map((p) => (
            <a key={p.domain} href={p.href} target="_blank" rel="noreferrer">
              {p.domain}
            </a>
          ))}
        </div>
      </div>

      {/* What we do */}
      <section className="ac-band ac-paper" id="services">
        <div className="ac-wrap">
          <div className="ac-center">
            <span className="ac-eyebrow ac-reveal">What we do</span>
            <h2 className="ac-h2 ac-reveal" style={{ marginTop: 18 }}>
              Four things, done properly.
            </h2>
            <div className="ac-rule mid ac-reveal" />
            <p className="ac-lead mid ac-reveal">
              We are a small team that builds complete systems rather than pieces of
              them — so the thing you get actually runs your organisation.
            </p>
          </div>

          <div className="ac-serv">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className={`ac-serv-card ac-reveal d${i}`}>
                  <div className="ac-serv-ic">
                    <Icon size={23} />
                  </div>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* School Portal — our own product.
          In the restructure this uses the page's OWN dark band instead of the
          product's navy: the colour arrives in the screenshot and in small
          accents, so the section belongs to this page while still being
          unmistakably School Portal. */}
      <section className={`ac-band ${next ? 'ac-dark acx-product' : 'ac-sp'}`} id="product">
        <div className="ac-wrap">
          <div className="ac-sp-grid">
            <div>
              <span className="ac-eyebrow ac-reveal" style={{ color: '#9BE08E' }}>
                {SCHOOL_PORTAL.eyebrow}
              </span>
              <img
                className="ac-sp-logo ac-reveal"
                src="/pitches/schoolportal/brand/logo-white.png"
                alt={SCHOOL_PORTAL.name}
                width={290}
                height={41}
              />
              <span className="ac-sp-tag ac-reveal">{SCHOOL_PORTAL.tagline}</span>
              <h2 className="ac-reveal">
                {SCHOOL_PORTAL.headline} <em>{SCHOOL_PORTAL.scope}</em>
              </h2>
              <p className="ac-sp-lede ac-reveal">{SCHOOL_PORTAL.lede}</p>

              <div className="ac-sp-actions ac-reveal">
                <a className="ac-btn ac-btn-sp" href={SCHOOL_PORTAL.pitchHref}>
                  {SCHOOL_PORTAL.pitchLabel}
                  <ArrowRight size={17} />
                </a>
                <a className="ac-btn ac-btn-sp-line" href={ENQUIRE}>
                  Talk to us about your school
                </a>
              </div>

              <div className="ac-sp-proof ac-reveal">
                <strong>Running today at</strong>
                {SCHOOL_PORTAL.deployments.map((d) => (
                  <a key={d.href} href={d.href}>
                    {d.school}
                  </a>
                ))}
              </div>
            </div>

            {next ? (
              <div className="acx-sp-media ac-reveal d1">
                <span className="ac-case-glow" style={{ background: SCHOOL_PORTAL.colors.navy }} />
                <BrowserFrame
                  image="schoolportal.png"
                  src={images['schoolportal']?.url}
                  domain="School Portal"
                  accent={SCHOOL_PORTAL.colors.navy}
                  alt="School Portal — one platform for every role in a school"
                />
              </div>
            ) : (
              <div className="ac-sp-points">
                {SCHOOL_PORTAL.points.map((pt, i) => (
                  <div key={pt.title} className={`ac-sp-point ac-reveal d${i}`}>
                    <b>{pt.title}</b>
                    <span>{pt.body}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {next && (
            <>
              <div className="acx-points">
                {SCHOOL_PORTAL.points.map((pt, i) => (
                  <div key={pt.title} className={`acx-point ac-reveal d${i}`}>
                    <b>{pt.title}</b>
                    <span>{pt.body}</span>
                  </div>
                ))}
              </div>

              <div className="acx-also">
                <span className="ac-eyebrow ac-reveal">Also ours</span>
                <h3 className="ac-h3 ac-reveal" style={{ marginTop: 12 }}>
                  Products we build and run ourselves.
                </h3>
                <div className="acx-grid">
                  {PROJECTS.filter((p) => p.kind === 'product').map((p) => (
                    <ProductCard key={p.id} project={p} src={images[p.id]?.url} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* the product's own wave — only on the product-coloured band; on the
            page's dark band it would be a stripe of someone else's brand */}
        {!next && (
        <svg className="ac-sp-wave" viewBox="0 0 1200 190" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="acSpB" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor="#1553D6" />
              <stop offset="1" stopColor="#4C86F0" />
            </linearGradient>
            <linearGradient id="acSpG" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor="#017D45" />
              <stop offset=".55" stopColor="#2CAA34" />
              <stop offset="1" stopColor="#7FD36B" />
            </linearGradient>
          </defs>
          <path d="M0 40 C 180 70 300 120 470 125 C 330 150 150 170 0 190 Z" fill="url(#acSpB)" />
          <path
            d="M40 150 C 360 70 760 60 1200 150 L 1200 165 C 780 88 380 100 40 162 Z"
            fill="url(#acSpG)"
          />
        </svg>
        )}
      </section>

      {/* B — the rest of the product line, each in its own colour */}
      {brands && (
        <div id="products">
          {PROJECTS.filter((p) => p.kind === 'product').map((p, i) => (
            <BrandBand key={p.id} project={p} index={i} src={images[p.id]?.url} />
          ))}
        </div>
      )}

      {/* Selected work */}
      <section className="ac-band ac-white" id="work">
        <div className="ac-wrap">
          <div className="ac-center" style={{ marginBottom: 'clamp(52px,6vw,84px)' }}>
            <span className="ac-eyebrow ac-reveal">
              {next || brands ? 'Built for clients' : 'Selected work'}
            </span>
            <h2 className="ac-h2 ac-reveal" style={{ marginTop: 18 }}>
              Systems people use every working day.
            </h2>
            <div className="ac-rule mid ac-reveal" />
            <p className="ac-lead mid ac-reveal">
              {next || brands
                ? 'Platforms we designed and built for named clients — all of them live, all of them maintained by the people who wrote them.'
                : 'Platforms we designed and built for schools, businesses and our own product line — all of them live, all of them maintained by the people who wrote them.'}
            </p>
          </div>

          {(next || brands ? PROJECTS.filter((p) => p.kind === 'client') : PROJECTS).map((p, i) => (
            <Case key={p.id} project={p} index={i} src={images[p.id]?.url} />
          ))}
        </div>
      </section>

      {/* How we work */}
      <section className="ac-band ac-dark" id="approach">
        <div className="ac-wrap">
          <div className="ac-center">
            <span className="ac-eyebrow ac-reveal">How we work</span>
            <h2 className="ac-h2 ac-reveal" style={{ marginTop: 18 }}>
              From how you work today to software that fits it.
            </h2>
            <div className="ac-rule mid ac-reveal" />
          </div>

          <div className="ac-steps">
            {APPROACH.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.step} className="ac-step">
                  <span className="ac-step-n">{s.step}</span>
                  <h3>
                    <Icon size={19} style={{ color: '#ff6b35' }} /> {s.title}
                  </h3>
                  <p>{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="ac-band ac-paper">
        <div className="ac-wrap">
          <div className="ac-stats">
            {STATS.map((s) => (
              <div key={s.label} className="ac-stat ac-reveal">
                <div
                  className="ac-stat-n"
                  data-count={s.value}
                  data-suffix={s.suffix ?? ''}
                >
                  0{s.suffix ?? ''}
                </div>
                <div className="ac-stat-l">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="ac-center" style={{ marginTop: 'clamp(56px,6vw,80px)' }}>
            <span className="ac-eyebrow ac-reveal">Built with</span>
            <div className="ac-tech ac-reveal">
              {STACK.map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="ac-band ac-white">
        <div className="ac-wrap">
          <div className="ac-center">
            <span className="ac-eyebrow ac-reveal">Why Acepsle</span>
            <h2 className="ac-h2 ac-reveal" style={{ marginTop: 18 }}>
              Local isn’t a compromise. It’s the advantage.
            </h2>
            <div className="ac-rule mid ac-reveal" />
          </div>

          <div className="ac-why">
            {WHY.map((w, i) => (
              <div key={w.title} className={`ac-why-item ac-reveal d${i % 2}`}>
                <h3>{w.title}</h3>
                <p>{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="ac-band ac-cta-band" id="contact">
        <span className="ac-glow ac-glow-1" />
        <div className="ac-wrap ac-cta-inner">
          <span className="ac-eyebrow ac-reveal">Start something</span>
          <h2 className="ac-h2 ac-reveal" style={{ marginTop: 18, color: '#fff' }}>
            Tell us what you’re trying to run better.
          </h2>
          <p className="ac-lead mid ac-reveal" style={{ marginTop: 18 }}>
            A school, a clinic, a workshop, a whole company — if it runs on
            spreadsheets and WhatsApp today, there is a system in it. Send us the
            problem and we’ll tell you honestly what it would take.
          </p>
          <div className="ac-cta-row ac-reveal">
            <a className="ac-btn ac-btn-brand" href={ENQUIRE}>
              Start a project <ArrowRight size={16} />
            </a>
            <button className="ac-btn ac-btn-line" onClick={onSignIn}>
              Sign in to the Hub
            </button>
          </div>
          <div className="ac-cta-alt ac-reveal">
            <a href={`mailto:${COMPANY.email}`}>
              <Mail size={15} /> {COMPANY.email}
            </a>
            <a href={`tel:${COMPANY.phoneHref}`}>
              <Phone size={15} /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="ac-foot">
        <div className="ac-wrap">
          <div className="ac-foot-grid">
            <div className="ac-foot-brand">
              <AcepsleWordmark height={28} variant="reversed" />
              <p>
                A Gaborone software company building web and mobile platforms for
                schools, businesses and people across Botswana — designed, built and
                supported right here.
              </p>
            </div>

            <div className="ac-foot-col">
              <h4>Our products</h4>
              {PROJECTS.filter((p) => p.kind === 'product').map((p) =>
                p.href ? (
                  <a key={p.name} href={p.href} target="_blank" rel="noreferrer">
                    {p.name}
                  </a>
                ) : (
                  <span key={p.name}>{p.name}</span>
                ),
              )}
            </div>

            <div className="ac-foot-col">
              <h4>Client platforms</h4>
              {PROJECTS.filter((p) => p.kind === 'client').map((p) => (
                <a key={p.name} href={p.href} target="_blank" rel="noreferrer">
                  {p.name}
                </a>
              ))}
            </div>

            <div className="ac-foot-col">
              <h4>Contact</h4>
              <a
                href="https://maps.google.com/?q=Plot+37043+Block+8+Gaborone"
                target="_blank"
                rel="noreferrer"
              >
                <MapPin size={15} /> {COMPANY.address}
              </a>
              <a href={`mailto:${COMPANY.email}`}>
                <Mail size={15} /> {COMPANY.email}
              </a>
              <a href={`tel:${COMPANY.phoneHref}`}>
                <Phone size={15} /> {COMPANY.phone}
              </a>
              <span>{COMPANY.phoneAlt}</span>
            </div>
          </div>

          <div className="ac-foot-bar">
            <div className="ac-foot-bar-row">
              {/* The registered name and number, as on the certificate — what a
                  business-verification reviewer looks for on the company site. */}
              <span>
                © {new Date().getFullYear()} {COMPANY.legalName} (trading as{' '}
                {COMPANY.displayName}) · Company reg. {COMPANY.registration}
              </span>
              <span className="ac-foot-legal">
                <a href="/privacy">Privacy</a>
                <a href="/terms">Terms</a>
                <a href="/data-deletion">Data deletion</a>
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
